import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { GET_USER_ACCOUNT_TOKENS } from "../../graphql/queries";
import { API_BASE } from "../../utils/api/api";
import styles from "./ConnectModal.module.css";

const META_APP_ID = import.meta.env.VITE_META_APP_ID;

const META_REDIRECT_URI = `${API_BASE}/facebook/callback`;
const META_SCOPES = [
  "email",
  "public_profile",
  "read_insights",
  "business_management",
  "ads_read",
  "ads_management",
  "pages_show_list",
  "pages_read_engagement",
  "pages_manage_posts",
  "instagram_basic",
  "instagram_content_publish",
  
].join(",");

const MetaConnectModal = ({ visible, onClose, onConnected }) => {
  const { t } = useLocalization();
  const { user } = useAuthUser();
  const authToken = user?.token;
  const userId = user?._id || user?.id || user?.userId;

  const [isConnecting, setIsConnecting] = useState(false);
  const [status, setStatus] = useState(null);
  const pollIntervalRef = useRef(null);
  const oauthWindowRef = useRef(null);

  const { data: tokensData, refetch: refetchTokens } = useQuery(
    GET_USER_ACCOUNT_TOKENS,
    {
      skip: !authToken,
      fetchPolicy: "network-only",
      context: {
        headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
      },
    }
  );

  const isFacebookConnected = Boolean(
    tokensData?.getUserAccountTokens?.facebookToken
  );

  // Poll for connection status while modal is visible and connecting
  useEffect(() => {
    if (!visible || !isConnecting) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const result = await refetchTokens();
        const connected = Boolean(
          result?.data?.getUserAccountTokens?.facebookToken
        );
        if (connected) {
          setIsConnecting(false);
          setStatus(t("MetaConnectScreen.connected") || "Connected!");
          onConnected?.();
          setTimeout(() => onClose?.(), 1500);
        }
      } catch (e) {
        console.log("refetchTokens error:", e?.message || e);
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [visible, isConnecting, refetchTokens, onClose, onConnected, t]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const buildAuthUrl = useCallback(() => {
    const state = encodeURIComponent(userId || "");
    return (
      `https://www.facebook.com/v23.0/dialog/oauth?client_id=${META_APP_ID}` +
      `&redirect_uri=${encodeURIComponent(META_REDIRECT_URI)}` +
      `&response_type=code&scope=${encodeURIComponent(META_SCOPES)}` +
      `&state=${state}`
    );
  }, [userId]);

  const startOAuth = useCallback(() => {
    if (!userId) {
      setStatus(t("MetaConnectScreen.oauthUserMissing") || "User missing");
      setIsConnecting(false);
      return;
    }

    setIsConnecting(true);
    setStatus(t("MetaConnectScreen.connecting") || "Connecting...");

    const authUrl = buildAuthUrl();
    oauthWindowRef.current = window.open(authUrl, "_blank", "noopener,width=600,height=700");

    // Check if window was closed
    const checkClosed = setInterval(() => {
      if (oauthWindowRef.current?.closed) {
        clearInterval(checkClosed);
        // Keep polling for a bit after window closes
        setTimeout(() => {
          if (!isFacebookConnected) {
            setStatus(t("MetaConnectScreen.checkingConnection") || "Checking connection...");
          }
        }, 1000);
      }
    }, 500);
  }, [userId, buildAuthUrl, t, isFacebookConnected]);

  if (!visible) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose} type="button">
          &times;
        </button>

        <div className={styles.iconBox} style={{ backgroundColor: "#EEF2FF" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#1877F2">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        </div>

        <h2 className={styles.title}>
          {t("MetaConnectScreen.headerTitle") || "Connect Meta"}
        </h2>
        <p className={styles.subtitle}>
          {t("MetaConnectScreen.subtitle") ||
            "Connect your Meta account to enable Facebook Ads, Instagram, and Business Manager integration."}
        </p>

        <button
          className={`${styles.primaryBtn} ${styles.metaBtn}`}
          onClick={startOAuth}
          disabled={isConnecting}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8 }}>
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          {isConnecting
            ? t("MetaConnectScreen.connecting") || "Connecting..."
            : isFacebookConnected
            ? t("MetaConnectScreen.connected") || "Meta Connected"
            : t("MetaConnectScreen.connect") || "Connect Meta"}
        </button>

        {(status || isConnecting) && (
          <div className={styles.statusRow}>
            {isConnecting && <div className={styles.spinner} />}
            {status && <span className={styles.statusText}>{status}</span>}
          </div>
        )}

        <div className={styles.tipBox}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#1D4ED8">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          <span className={styles.tipText}>
            {t("MetaConnectScreen.tip") ||
              "This connects Facebook Ads, Instagram, and Business Manager to provide advertising insights and social media analytics."}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MetaConnectModal;
